import React, { Component } from "react";
import { oneOfType, array, string, func, bool, number, shape, arrayOf } from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { push } from "react-router-redux";

import { Box, Button } from "@ui-kit/components/index.es";

import { actions as depositsActions, selectors as depositsSelectors } from "reducers/deposits";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as holidaysActions } from "reducers/holidays";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import Deposit from "pages/deposits/_components/Deposit";
import GeneralMsg from "pages/_components/GeneralMsg";
import Container from "pages/_components/Container";
import * as i18n from "util/i18n";
import EquivalentTotalBalance from "pages/accounts/_components/EquivalentTotalBalance";
import Pagination from "pages/_components/pagination/Pagination";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

import * as configUtil from "util/config";

class ProductList extends Component {
    static propTypes = {
        dispatch: func.isRequired,
        fetching: oneOfType([string, bool]),
        deposits: oneOfType([array]),
        holidays: arrayOf(string),
        ARSTotalBalance: number,
        USDTotalBalance: number,
        isDesktop: bool.isRequired,
        isSmallDesktop: bool.isRequired,
        fetchingDownload: bool.isRequired,
        activeEnvironment: shape({}).isRequired,
        totalPages: number.isRequired,
        pageNumber: number.isRequired,
        embeddedInV3Shell: bool,
    };

    static defaultProps = {
        fetching: false,
        deposits: [],
        holidays: [],
        ARSTotalBalance: null,
        USDTotalBalance: null,
        embeddedInV3Shell: false,
    };

    componentDidMount() {
        const { dispatch } = this.props;
        dispatch(depositsActions.listDeposits());
        dispatch(holidaysActions.listHolidays());
        this.handlePlazoFijoAvailable();
        this.syncEmbeddedV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { embeddedInV3Shell, isDesktop, fetchingDownload, fetching, deposits, activeEnvironment } = this.props;
        const shellOrViewportChanged =
            embeddedInV3Shell &&
            (prevProps.embeddedInV3Shell !== embeddedInV3Shell || prevProps.isDesktop !== isDesktop);
        const mobileHeaderContentDepsChanged =
            embeddedInV3Shell &&
            !isDesktop &&
            (prevProps.fetchingDownload !== fetchingDownload ||
                prevProps.fetching !== fetching ||
                prevProps.deposits !== deposits ||
                prevProps.activeEnvironment !== activeEnvironment);

        if (shellOrViewportChanged || mobileHeaderContentDepsChanged) {
            this.syncEmbeddedV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { embeddedInV3Shell, dispatch } = this.props;
        if (embeddedInV3Shell) {
            clearEmbeddedV3ShellLayoutState(dispatch);
        }
    }

    syncEmbeddedV3ShellLayout = () => {
        const { dispatch, embeddedInV3Shell, isDesktop } = this.props;
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["deposits"],
            titleKey: "menu.deposits",
            goBackPath: "/v3/dashboard",
            rightContentMobile: !isDesktop ? this.rightContentMobile() : undefined,
        });
    };

    rightContentMobile = () => {
        const { deposits, activeEnvironment, fetching, fetchingDownload } = this.props;
        const canCreate = !(isEmpty(deposits) && !activeEnvironment.permissions.depositsConstitute);
        return (
            <Box flex align="center" gap={4}>
                {canCreate && (
                    <Button
                        icon="Add"
                        iconVariant="outline"
                        color="white"
                        variant="text"
                        onClick={this.handleBuildDepositClick}
                        aria-label={i18n.get("deposits.page.newDeposit")}
                        isInactive={fetching}
                        css={{ padding: 8 }}
                    />
                )}
                <Button
                    icon="SendDown"
                    iconVariant="outline"
                    color="white"
                    variant="text"
                    onClick={() => this.handleExport("pdf")}
                    aria-label={i18n.get("deposits.list.download")}
                    isInactive={fetchingDownload}
                    loading={fetchingDownload}
                    size="medium"
                    css={{ padding: 8 }}
                />
            </Box>
        );
    };

    handlePlazoFijoAvailable = () => {
        const { dispatch, activeEnvironment } = this.props;
        const plazoFijoDisponible = configUtil.getBoolean("frontend.constitucionPlazoFijo.disponible", false);
        if (!plazoFijoDisponible && activeEnvironment.permissions.depositsConstitute) {
            const textoDisponible = configUtil.get("frontend.constitucionPlazoFijo.textoDisponible", "");
            dispatch(notificationActions.showNotification(textoDisponible, "warning", ["deposits"], null, false));
            return false;
        }
        return true;
    };

    handleExport = (format) => {
        const { dispatch } = this.props;
        dispatch(depositsActions.downloadList(format));
    };

    handleBack = () => {
        const { dispatch, embeddedInV3Shell } = this.props;
        if (embeddedInV3Shell) {
            dispatch(push("/v3/dashboard"));
            return;
        }
        dispatch(push("/desktop/"));
    };

    handleBuildDepositClick = () => {
        if (this.handlePlazoFijoAvailable()) {
            const { dispatch } = this.props;
            dispatch(depositsActions.checkHours());
        }
    };

    renderHeader = () => {
        const { deposits, isDesktop, fetching, fetchingDownload, activeEnvironment } = this.props;
        const rightContentResult =
            isEmpty(deposits) && !activeEnvironment.permissions.depositsConstitute ? <div /> : null;

        return (
            <>
                <div className="admin-detail-head px-0">
                    {isDesktop && <Head onBack={this.handleBack} />}
                    <Head
                        title="menu.deposits"
                        addLinkToLabel={isDesktop ? "deposit.new" : null}
                        handleCreateMessage={isDesktop ? "deposits.page.newDeposit" : ""}
                        exportList
                        handleClick={this.handleExport}
                        isFetchingExport={fetchingDownload}
                        onBack={!isDesktop ? this.handleBack : null}
                        handleClickMessage="deposits.list.download"
                        csvDownload={isDesktop}
                        xlsDownload={isDesktop}
                        downloadImageWhite={!isDesktop}
                        addButtonImageWhite={!isDesktop}
                        titleClassName={!isDesktop ? "header-mobile-title-background-blue" : ""}
                        headerClassName={!isDesktop && "blue-main-header-mobile blue-main-title-mobile"}
                        hideMobileMenu={!isDesktop}
                        handleNewClick={
                            !fetching &&
                            activeEnvironment.permissions.depositsConstitute &&
                            this.handleBuildDepositClick
                        }
                        newButtonLabel={
                            isDesktop && activeEnvironment.permissions.depositsConstitute ? "deposit.new" : null
                        }
                        rightContent={rightContentResult && (() => rightContentResult)}
                        imageStyle="mr-2"
                    />
                </div>
            </>
        );
    };

    fetchNextPage = (pageNumber) => {
        const { dispatch } = this.props;
        dispatch(depositsActions.setPageNumber(pageNumber));
        dispatch(depositsActions.listDeposits());
    };

    render() {
        const { deposits, fetching, isDesktop, isSmallDesktop, totalPages, pageNumber, embeddedInV3Shell } = this.props;
        const maxBoxDisplay = configUtil.getInteger("product.list.maxBoxDisplay", 5);

        const productAsCard = deposits.length < maxBoxDisplay;

        return (
            <>
                {!embeddedInV3Shell && <Notification scopeToShow="deposits" notificationClassname="navbar-fixed-top" />}
                {!isDesktop && !embeddedInV3Shell && this.renderHeader()}
                <PageLoading loading={fetching} className="screen-loader">
                    {!fetching && isDesktop && this.renderHeader()}
                    <div className="above-the-fold">
                        <EquivalentTotalBalance {...this.props} title="deposits.balance.total" />
                        {deposits.length ? (
                            <>
                                <Container className="align-items-center flex-grow">
                                    <div className="table table--products">
                                        <div
                                            role="menu"
                                            className={classNames("table-body", {
                                                "table-body--grid": productAsCard,
                                            })}>
                                            {deposits && deposits.length > 0 && (
                                                <>
                                                    {Object.entries(deposits).map(([id, deposit]) => (
                                                        <Deposit
                                                            deposit={deposit}
                                                            key={`${id} - ${deposit.depositNumber}`}
                                                            productAsCard={productAsCard}
                                                            isDesktop={isDesktop}
                                                            isSmallDesktop={isSmallDesktop}
                                                        />
                                                    ))}
                                                    <div className="d-flex w-100 justify-content-end">
                                                        <Pagination
                                                            totalPages={totalPages}
                                                            pageNumber={pageNumber}
                                                            action={this.fetchNextPage}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Container>
                            </>
                        ) : (
                            <GeneralMsg
                                imagePath="images/coloredIcons/find-in-page.svg"
                                description={i18n.get("deposits.list.empty")}
                                isDesktop={isDesktop}
                            />
                        )}
                    </div>
                </PageLoading>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    deposits: depositsSelectors.getDeposits(state),
    ARSTotalBalance: depositsSelectors.getARSTotalBalance(state),
    USDTotalBalance: depositsSelectors.getUSDTotalBalance(state),
    fetching: depositsSelectors.getFetching(state),
    fetchingDownload: depositsSelectors.getFetchingDownload(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    pageNumber: depositsSelectors.getPageNumber(state),
    totalPages: depositsSelectors.getTotalPages(state),
});

export default connect(mapStateToProps)(resizableRoute(ProductList));
